import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { RJSFSchema } from '@rjsf/utils'
import { merge, orderBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import ErrorComponent from '@/components/ErrorComponent'
import Loading from '@/components/Loading'
import Button from '@/components/TailwindUI/Button'
import useMessage from '@/hooks/useMessage'
import { WorkgridDatasourceForm } from '@/pages/SpaceAppBuilder/components/WorkgridDatasourceForm'
import { extractUiSchema } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/components/Form'
import {
  AppInstanceDataSource,
  DatasourceWizardMode,
  WorkgridCatalogDatasource,
  WorkgridDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import {
  dataSourceV3QueryOptions,
  datasourceV3WithAppQueryOptions,
  getDataSourceConfigurationQueryOptions,
  replaceV3DataSourceWithAppQueryOptions
} from '@/pages/SpaceAppBuilder/utilities/queries'
import { getAccessToken } from '@/store/contextSubscriber'

import WorkgridDataSourceSwitcher from './WorkgridDataSourceSwitcher'

const ErrorMessage = styled.p`
  color: red;
  font-weight: bold;
  font-size: 1rem;
  margin-bottom: 5px;
`

const ErrorSubMessage = styled.span`
  font-size: 13px;
  color: #707070;
`
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: auto;
  margin-left: auto;
  max-height: 500px;
`

const WorkgridDatasourceConfigurationForm = ({
  datasourceKey,
  mode,
  currentUserOwnedWorkgridDataSource,
  currentApplicationDatasource,
  showInstanceSelection,
  onClose,
  onBack,
  appId = '',
  testDatasourceInstance,
  onSave
}: {
  datasourceKey: string
  appId?: string
  showInstanceSelection: boolean
  mode: DatasourceWizardMode
  currentUserOwnedWorkgridDataSource?: WorkgridDatasource
  currentApplicationDatasource?: AppInstanceDataSource
  onClose?: () => void
  onBack?: () => void
  testDatasourceInstance?: boolean
  onSave?: (datasource: WorkgridDatasource) => void
}) => {
  const { spaceId } = useParams<{ spaceId: string }>()

  // The configuration for this datasource (what fields to display in the form)
  const {
    isLoading: isLoadingDataSourceConfiguration,
    data: dataSourceConfiguration,
    error: dataSourceConfigurationError
  } = useQuery<WorkgridCatalogDatasource, Error>({
    ...getDataSourceConfigurationQueryOptions({
      spaceId,
      dataSourceId: datasourceKey
    }),
    enabled: !!datasourceKey
  })

  const [error, setError] = useState<any>()
  const [isPendingUserAuthConnection, setIsPendingUserAuthConnection] = useState(false)
  const [formMode, setFormMode] = useState(mode)
  const [selectedWorkgridDataSourceInstance, setSelectedWorkgridDataSourceInstance] = useState<
    WorkgridDatasource | undefined
  >(currentUserOwnedWorkgridDataSource)
  const dataSourcesV3Query = useQuery<WorkgridDatasource[]>({
    ...dataSourceV3QueryOptions({ spaceId, datasourceId: datasourceKey }),
    enabled: formMode === DatasourceWizardMode.EDIT || formMode === DatasourceWizardMode.IMPORT,
    select: data => (testDatasourceInstance ? data.filter(ds => ds.configuration?.auth === 'oauth2') : data)
  })

  const dataSourceMutation = useMutation<any, { data: string }>({})
  useEffect(() => setFormMode(mode), [mode])

  const handleChange = (value: WorkgridDatasource) => {
    setSelectedWorkgridDataSourceInstance(value)
    onSave && onSave(value)
  }
  useMessage('workgrid-auth', (send, payload) => {
    if (payload.success) {
      if (testDatasourceInstance) {
        setFormMode(DatasourceWizardMode.EDIT)
      } else {
        onClose && onClose()
      }
      setIsPendingUserAuthConnection(false)
    } else {
      setError({
        message: 'Failed to authenticate. Please check your credentials or configuration',
        subMessage: (payload.error as any).data
      })
      setIsPendingUserAuthConnection(false)
    }
  })

  async function executeMutation({
    query,
    method,
    body
  }: {
    query: string
    method: 'POST' | 'PUT' | 'DELETE'
    body?: object
  }): Promise<any> {
    return await dataSourceMutation.mutateAsync(
      // @ts-expect-error
      [
        query,
        {
          headers: { 'content-type': 'application/json' },
          method,
          body: JSON.stringify(body)
        }
      ],
      {
        onSuccess: async () => {
          await dataSourcesV3Query.refetch()
        }
      }
    )
  }

  async function createV3DataSource({ body }: { body: object }) {
    return executeMutation({
      query: dataSourceV3QueryOptions({ spaceId, datasourceId: datasourceKey }).queryKey[0],
      method: 'POST',
      body
    })
  }

  const buildSchema = (schema: Record<string, any>): RJSFSchema => ({
    type: 'object',
    properties: {
      name: {
        title: 'Name',
        type: 'string'
      },
      configuration: {
        title: 'Configuration',
        ...schema
      }
    }
  })
  const buildUiSchema = (schema: Record<string, any>) =>
    merge(extractUiSchema(buildSchema(schema)), {
      'ui:options': {
        submitButtonOptions: {
          submitText: 'Save',
          props: { disabled: false, className: '' }
        }
      },
      configuration: {
        callbackUrl: {
          'ui:widget': 'CallBackUrl'
        }
      }
    })

  async function handleSubmit(values: { formData: { name: any; configuration: any; auth: string } }) {
    if (
      (formMode === DatasourceWizardMode.EDIT || formMode === DatasourceWizardMode.IMPORT) &&
      !selectedWorkgridDataSourceInstance
    ) {
      return
    }
    if (selectedWorkgridDataSourceInstance && testDatasourceInstance) {
      localStorage.setItem(
        `testOauthDatasource-${datasourceKey}-${spaceId}`,
        JSON.stringify(selectedWorkgridDataSourceInstance)
      )
      onClose?.()
    }

    if (formMode === DatasourceWizardMode.CREATE) {
      const datasourceDetails = {
        name: values.formData.name,
        configuration: values.formData.configuration
      }
      const configurationValues = values.formData
      const isOauth = values.formData.configuration.auth === 'oauth2'

      if (isOauth) {
        setIsPendingUserAuthConnection(true)

        const configuration = encodeURIComponent(JSON.stringify(configurationValues.configuration))
        let param
        if (appId && !testDatasourceInstance) {
          param = `spaceId=${spaceId}&datasourceId=${datasourceKey}&name=${datasourceDetails.name}&applicationId=${appId}&configuration=${configuration}`
        } else {
          param = `spaceId=${spaceId}&datasourceId=${datasourceKey}&name=${datasourceDetails.name}&configuration=${configuration}`
        }
        const encodedParam = encodeURIComponent(param)
        const authorizeWindow = window.open(
          `/console/datasource/callback?${encodedParam}#token=${getAccessToken()}`,
          '_blank'
        )
        authorizeWindow?.focus()
      } else {
        const response = await createV3DataSource({ body: datasourceDetails })

        if (appId) {
          await executeMutation({
            query: replaceV3DataSourceWithAppQueryOptions({
              spaceId,
              appId,
              dataSourceId: datasourceKey,
              datasourceInstanceId: response.id
            }).queryKey[0],
            method: 'POST'
          })
        }
        onClose?.()
      }
    } else if (selectedWorkgridDataSourceInstance && !!appId && !testDatasourceInstance) {
      if (currentApplicationDatasource) {
        await executeMutation({
          query: replaceV3DataSourceWithAppQueryOptions({
            spaceId,
            appId,
            dataSourceId: datasourceKey,
            datasourceInstanceId: selectedWorkgridDataSourceInstance.id
          }).queryKey[0],
          method: 'POST'
        })
      } else {
        await executeMutation({
          query: datasourceV3WithAppQueryOptions({
            spaceId,
            appId,
            dataSourceId: datasourceKey,
            datasourceInstanceId: selectedWorkgridDataSourceInstance.id
          }).queryKey[0],
          method: 'POST'
        })
      }
      onClose?.()
    }
  }

  return isLoadingDataSourceConfiguration ||
    dataSourcesV3Query.isLoading ||
    dataSourceMutation.isLoading ||
    isPendingUserAuthConnection ? (
    <LoadingContainer>
      <Loading
        isLoading={true}
        loadingMessage={
          isPendingUserAuthConnection
            ? `Redirecting to ${dataSourceConfiguration?.name}...`
            : dataSourceMutation.isLoading
              ? 'Saving...'
              : 'Loading...'
        }
      />
    </LoadingContainer>
  ) : dataSourceConfigurationError ? (
    <ErrorComponent errors={[{ title: 'Failed to load data source configuration, please reload and try again.' }]} />
  ) : (
    <>
      <div className="mt-2 flex flex-col space-y-2">
        {dataSourceConfiguration?.supportUrl && (
          <a
            href={dataSourceConfiguration?.supportUrl}
            rel="noopener noreferrer"
            target="_blank"
            className="absolute right-[80px] -mt-[55px] mr-8 hidden items-center font-medium text-blue-500 hover:underline dark:text-blue-500 sm:flex"
          >
            Credentials and Config Help
            <ArrowTopRightOnSquareIcon
              aria-hidden="true"
              className="ml-1.5 mb-0.5 h-4 w-4 flex-shrink-0 text-blue-500 hover:underline dark:text-blue-500"
            />
          </a>
        )}
      </div>
      <WorkgridDatasourceForm
        schema={formMode === DatasourceWizardMode.CREATE ? buildSchema(dataSourceConfiguration?.schema || {}) : {}}
        uiSchema={buildUiSchema(dataSourceConfiguration?.schema || {})}
        onSubmit={handleSubmit}
        className="install-form group max-h-[475px] w-full"
      >
        <>
          {dataSourceConfiguration &&
            (formMode === DatasourceWizardMode.EDIT || formMode === DatasourceWizardMode.IMPORT) && (
              <WorkgridDataSourceSwitcher
                isWorkgridDataSource={true}
                dataSources={orderBy(dataSourcesV3Query.data, 'name') || []}
                dataSourceConfiguration={dataSourceConfiguration}
                value={
                  selectedWorkgridDataSourceInstance ||
                  dataSourcesV3Query?.data?.find(ds => ds.id === currentUserOwnedWorkgridDataSource?.id)
                }
                errorMessage={
                  selectedWorkgridDataSourceInstance ? '' : 'Please select an instance, or create a new one'
                }
                onAddNewInstance={() => setFormMode(DatasourceWizardMode.CREATE)}
                onChange={(v: WorkgridDatasource) => {
                  handleChange(v)
                }}
              />
            )}
          {dataSourceMutation.error && (
            <div>
              <ErrorMessage>{'Failed to authenticate. Please check your credentials or configuration'}</ErrorMessage>
              <ErrorSubMessage>{dataSourceMutation.error.data || ''}</ErrorSubMessage>
            </div>
          )}
          {error && (
            <>
              <ErrorMessage>{error.message}</ErrorMessage>
              <ErrorSubMessage>{error.subMessage}</ErrorSubMessage>
            </>
          )}
          <div className="flex justify-end gap-4 p-2">
            {onBack && (
              <Button
                color="secondary"
                type="button"
                onClick={() => {
                  if (formMode === DatasourceWizardMode.CREATE && showInstanceSelection) {
                    setFormMode(DatasourceWizardMode.EDIT)
                  } else {
                    onBack()
                  }
                }}
              >
                Back
              </Button>
            )}

            <Button type="submit" color="primary">
              Save
            </Button>
          </div>
        </>
      </WorkgridDatasourceForm>
    </>
  )
}

export default WorkgridDatasourceConfigurationForm
